import { useScrollReveal } from '../../hooks/useScrollReveal';

const REASONS = [
  {
    icon: 'ri-shield-star-line',
    title: 'T.S.S.A. Certified',
    desc: 'Fully approved by Ontario\'s Technical Standards and Safety Authority — the highest HVAC credential in the province — ensuring code-compliant, safe work on every single job.',
    highlight: true,
  },
  {
    icon: 'ri-history-line',
    title: '20+ Years of Mastery',
    desc: 'Since 2002, we\'ve built deep expertise in GTA\'s unique climate, building codes, and residential challenges. Experience you feel from the first phone call.',
    highlight: false,
  },
  {
    icon: 'ri-time-line',
    title: '24/7 Emergency Response',
    desc: 'Heating and cooling emergencies don\'t keep business hours. Our dedicated GTA West and East teams are available around the clock — call anytime.',
    highlight: false,
  },
  {
    icon: 'ri-hand-heart-line',
    title: 'Honest, Pressure-Free Advice',
    desc: 'We assess your system and give you the truth — nothing more. No overselling, no scare tactics. Clients return because we consistently recommend what\'s right.',
    highlight: true,
  },
  {
    icon: 'ri-gift-2-line',
    title: 'Free In-Home Consultations',
    desc: 'Every new equipment inquiry starts with a complimentary in-home visit. We evaluate your space thoroughly and provide transparent recommendations at zero cost.',
    highlight: false,
  },
  {
    icon: 'ri-sparkling-line',
    title: 'Spotless Workmanship',
    desc: 'We protect floors, cover work areas, and leave every site cleaner than we found it. Our technicians treat your home with the same care as their own.',
    highlight: false,
  },
];

export default function WhyChooseUs() {
  const { ref, visible } = useScrollReveal<HTMLElement>();

  return (
    <section
      ref={ref}
      className="relative py-28 lg:py-36 overflow-hidden"
    >
      {/* Dark navy background */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/fc08b9d02d3b6a7367567b45de55d586.jpg"
          alt=""
          className="w-full h-full object-cover object-center opacity-12"
        />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(160deg, rgba(3,13,31,0.97) 0%, rgba(7,18,36,0.95) 50%, rgba(3,13,31,0.97) 100%)' }} />
      </div>
      <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 55% 70% at 80% 35%, rgba(26,101,224,0.08) 0%, transparent 65%)' }} />
      <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(26,101,224,0.25), transparent)' }} />

      <div className="relative max-w-[1500px] mx-auto px-6 lg:px-14">

        {/* Header + Stats Row */}
        <div
          className={`flex flex-col lg:flex-row lg:items-end justify-between gap-10 mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <div className="max-w-xl">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-8 h-0.5 bg-luxury-gold" />
              <span className="font-caps text-[10px] tracking-[0.3em] uppercase text-white">Our Advantage</span>
            </div>
            <h2 className="font-serif text-5xl lg:text-7xl text-white leading-[1.0]">
              Why GTA Homeowners<br />
              <em className="not-italic text-luxury-gold">Choose Atlantis</em>
            </h2>
          </div>

          {/* Quick stats */}
          <div className="grid grid-cols-4 gap-0 border border-luxury-gold/25 rounded-2xl overflow-hidden">
            {[
              { val: '2002', lab: 'Founded' },
              { val: '4.9★', lab: 'HomeStars' },
              { val: '24/7', lab: 'Coverage' },
              { val: '6+', lab: 'GTA Cities' },
            ].map((s, i) => (
              <div
                key={s.val}
                className={`text-center py-5 px-5 ${i < 3 ? 'border-r border-luxury-gold/20' : ''}`}
                style={{ background: 'rgba(255,255,255,0.08)' }}
              >
                <div className="font-serif text-2xl text-luxury-gold font-bold whitespace-nowrap">{s.val}</div>
                <div className="font-caps text-[8px] tracking-[0.2em] uppercase text-white mt-1 whitespace-nowrap">{s.lab}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Reason Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {REASONS.map((r, i) => (
            <div
              key={r.title}
              className={`group relative rounded-2xl p-7 border transition-all duration-300 hover:-translate-y-1 cursor-default ${
                r.highlight
                  ? 'border-luxury-gold/35'
                  : 'border-white/15 hover:border-luxury-gold/35'
              } ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
              style={{
                background: r.highlight ? 'rgba(26,101,224,0.12)' : 'rgba(255,255,255,0.09)',
                backdropFilter: 'blur(14px)',
                transitionDelay: visible ? `${i * 90}ms` : '0ms',
              }}
            >
              {r.highlight && (
                <div className="absolute top-4 right-4">
                  <div className="w-2 h-2 rounded-full bg-luxury-gold/70 animate-pulse" />
                </div>
              )}

              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-luxury-gold/15 border border-luxury-gold/30 group-hover:bg-luxury-gold/25 transition-colors mb-5">
                <i className={`${r.icon} text-luxury-gold text-xl`} />
              </div>
              <h3 className="font-serif text-white text-xl font-semibold mb-3 group-hover:text-luxury-gold transition-colors">
                {r.title}
              </h3>
              <p className="font-sans text-sm text-white leading-relaxed">{r.desc}</p>
            </div>
          ))}
        </div>

        {/* Guarantee strip */}
        <div
          className={`mt-12 rounded-2xl border border-luxury-gold/22 py-5 px-8 flex flex-wrap items-center justify-center gap-6 lg:gap-10 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
          style={{ background: 'rgba(26,101,224,0.08)', transitionDelay: '550ms' }}
        >
          {['Satisfaction Guarantee', 'No Hidden Fees', 'Free Consultations', 'Licensed & Insured', 'T.S.S.A. Approved'].map((g) => (
            <div key={g} className="flex items-center gap-2">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-checkbox-circle-fill text-luxury-gold text-sm" />
              </div>
              <span className="font-caps text-[10px] tracking-[0.15em] uppercase text-white/92 whitespace-nowrap">{g}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
